import BigNumber from 'bignumber.js';

import config from 'configs/app';
import { ZERO } from 'toolkit/utils/consts';

interface Params {
  value: string;
  exchangeRate?: string | null;
  accuracy?: number;
  accuracyUsd?: number;
  decimals?: string | null;
}

// [Basis MVP-NATIVE-1] Below this many decimals, an amount has nothing to
// abbreviate: every digit of it is displayable, so rounding one away is a
// straight loss of information rather than a kindness to the reader.
//
// Blockscout's call sites hardcode `accuracy: 8` (and `5` for the homepage fee
// column). At Ethereum's 18 decimals that is a sane abbreviation of a number
// nobody reads in full. At Basis's 9 it is lossy in both directions: a balance
// of 973902022 tomos rendered as 0.97390202, silently dropping the final digit,
// and a transfer of 7777777 tomos rendered as 0.00777778 -- rounded UP to a
// value the chain never held. Amounts here are integers of base units, so
// showing `decimals` places is exact by construction.
const FULL_PRECISION_MAX_DECIMALS = 9;

export default function getCurrencyValue({ value, accuracy, accuracyUsd, decimals, exchangeRate }: Params) {
  // [Basis MVP-NATIVE-1] The fallback is the CHAIN's precision, not Ethereum's.
  //
  // Callers that format a native amount mostly omit `decimals` -- transaction
  // value, transaction fee, block reward -- and upstream then divided by 1e18
  // regardless of what the chain actually uses. On Basis that reported a
  // 2,100,000-tomo fee as 0.0000000000021 LITHOS: right digits, wrong by nine
  // orders of magnitude. Only the few call sites that pass
  // `config.chain.currency.decimals` explicitly (the address balance) were
  // correct, which is why the error showed up on fees and values but not there.
  const decimalsNum = Number(decimals || config.chain.currency.decimals);
  const valueCurr = BigNumber(value).div(BigNumber(10).pow(decimalsNum));
  // Raise a requested accuracy to full precision when full precision is short
  // enough to show; leave 18-decimal tokens on the upstream abbreviation.
  const effectiveAccuracy = accuracy !== undefined && decimalsNum <= FULL_PRECISION_MAX_DECIMALS ?
    Math.max(accuracy, decimalsNum) :
    accuracy;
  const valueResult = effectiveAccuracy ? valueCurr.dp(effectiveAccuracy).toFormat() : valueCurr.toFormat();

  let usdResult: string | undefined;
  let usdBn = ZERO;

  if (exchangeRate) {
    const exchangeRateBn = new BigNumber(exchangeRate);
    usdBn = valueCurr.times(exchangeRateBn);
    if (accuracyUsd && !usdBn.isEqualTo(0)) {
      const usdBnDp = usdBn.dp(accuracyUsd);
      usdResult = usdBnDp.isEqualTo(0) ? usdBn.precision(accuracyUsd).toFormat() : usdBnDp.toFormat();
    } else {
      usdResult = usdBn.toFormat();
    }
  }

  return { valueCurr, valueStr: valueResult, usd: usdResult, usdBn };
}
